import { useMemo } from 'react'
import type { TechnicianScheduleView } from './scheduleView'

export interface ResolvedBreak {
  startTimeIso: string
  /** `null` marks a break the server projected from the profile — it has no row to drag yet. */
  overrideId: number | null
}

/**
 * The break for one day, as the server reported it.
 *
 * `breakOverrides` now carries both materialized BREAK entries and the default breaks the server
 * projects from the technician profile (those arrive with `id: null`). There is deliberately no
 * client-side projection left: the old fallback ignored `workingDays` — painting a phantom break
 * on a Saturday nobody works — and hardcoded both 12:00 and a 30-minute duration, so it disagreed
 * with the conflict engine that actually blocks bookings.
 *
 * A day with no entry at all means no break: a non-working day, which is a real answer.
 */
export function resolveBreakForDay(
  item: TechnicianScheduleView,
  dayIso: string,
): ResolvedBreak | null {
  const dayDate = new Date(dayIso)

  const entry = item.breakOverrides.find((candidate) => {
    const entryDate = new Date(candidate.startsAt)
    return (
      entryDate.getFullYear() === dayDate.getFullYear() &&
      entryDate.getMonth() === dayDate.getMonth() &&
      entryDate.getDate() === dayDate.getDate()
    )
  })

  if (!entry) return null

  return { startTimeIso: entry.startsAt, overrideId: entry.id }
}

export function useBreakBlocks(
  technicians: TechnicianScheduleView[] | undefined,
  dayIso: string,
): Map<string, ResolvedBreak> {
  return useMemo(() => {
    const map = new Map<string, ResolvedBreak>()
    if (!technicians) return map
    for (const tech of technicians) {
      const resolved = resolveBreakForDay(tech, dayIso)
      if (resolved) map.set(tech.technicianId, resolved)
    }
    return map
  }, [technicians, dayIso])
}
